import type { ComponentPropsWithRef } from "react";

import { cn } from "../lib/cn";
import { Button } from "./button";

export interface PaginationProps extends Omit<ComponentPropsWithRef<"nav">, "onChange"> {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
  /** Nombre de pages affichées de part et d'autre de la page courante. */
  siblings?: number;
}

const ELLIPSIS = "…" as const;

/**
 * Construit la liste des pages à afficher, avec des ellipses.
 * Ex. page 7 sur 20, siblings 1  →  1 … 6 7 8 … 20
 *
 * Extrait de la vue pour rester testable indépendamment : c'est la seule partie
 * réellement susceptible de se tromper.
 */
function buildRange(page: number, pageCount: number, siblings: number): (number | typeof ELLIPSIS)[] {
  // Première, dernière, la page courante, ses voisines et deux ellipses.
  const maxVisible = siblings * 2 + 5;
  if (pageCount <= maxVisible) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const left = Math.max(page - siblings, 1);
  const right = Math.min(page + siblings, pageCount);
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < pageCount - 1;

  const range: (number | typeof ELLIPSIS)[] = [1];
  if (showLeftEllipsis) range.push(ELLIPSIS);

  for (let i = showLeftEllipsis ? left : 2; i <= (showRightEllipsis ? right : pageCount - 1); i++) {
    range.push(i);
  }

  if (showRightEllipsis) range.push(ELLIPSIS);
  range.push(pageCount);
  return range;
}

/** Pagination numérotée. */
export function Pagination({
  className,
  page,
  pageCount,
  onChange,
  siblings = 1,
  ...props
}: PaginationProps) {
  if (pageCount <= 1) return null;
  const range = buildRange(page, pageCount, siblings);

  return (
    <nav aria-label="Pagination" className={cn("flex items-center gap-1", className)} {...props}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Page précédente"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronIcon direction="left" />
      </Button>

      {range.map((item, index) =>
        item === ELLIPSIS ? (
          <span key={`gap-${index}`} aria-hidden="true" className="px-1.5 text-sm text-fg-subtle">
            {ELLIPSIS}
          </span>
        ) : (
          <Button
            key={item}
            variant={item === page ? "subtle" : "ghost"}
            size="icon-sm"
            // `aria-current` est ce qui distingue la page courante à l'oral ;
            // la couleur seule ne suffirait pas.
            aria-current={item === page ? "page" : undefined}
            aria-label={`Page ${item}`}
            onClick={() => onChange(item)}
            className="tabular-nums"
          >
            {item}
          </Button>
        ),
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Page suivante"
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
      >
        <ChevronIcon direction="right" />
      </Button>
    </nav>
  );
}

function ChevronIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={direction === "left" ? "m12 5-5 5 5 5" : "m8 5 5 5-5 5"} />
    </svg>
  );
}
